import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthStore } from 'src/app/stores/auth.store';
import { get, includes } from 'lodash';
import { AbstractComponent } from '../shared/components/abstract-component';
import { DpopTokenService } from '../services/dpop-token.service';
import { AppConfigService } from '../services/app-config.service';
import { IAppConfig } from '../shared/interfaces/app-config';
import { Router } from '@angular/router';
import { CsrfService } from '../services/csrf.service';

/**
 * HTTP methods that modify state and require CSRF protection
 */
const CSRF_PROTECTED_METHODS: Set<string> = new Set<string>(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * HTTP status codes for unauthorized access
 */
const HTTP_STATUS_UNAUTHORIZED: number = 401;
const HTTP_STATUS_FORBIDDEN: number = 403;

/**
 * Position indicating token URL not found
 */
const TOKEN_NOT_FOUND: number = -1;

@Injectable({
  providedIn: 'root',
})
export class HeaderInterceptor extends AbstractComponent implements HttpInterceptor {

  constructor(
    private authStore: AuthStore,
    private dpopTokenService: DpopTokenService,
    private appConfigService: AppConfigService,
    private router: Router,
    private csrfService: CsrfService,
  ) {
    super();
  }

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const config: IAppConfig = this.appConfigService.getConfig();
    const impersonationToken: string = this.authStore.getImpersonationAccessToken();
    
    // Skip interceptor for config file
    if (includes(request.url, 'config.json')) {
      return next.handle(request);
    }

    // Build common headers including CSRF token for state-changing requests
    const csrfHeaders: Record<string, string> = this.getCsrfHeaders(request.method);

    if (request.url.startsWith('https://localhost')) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.authStore.getAccessToken()}`,
          Env: get(config, 'environment'),
          ...csrfHeaders,
          // ...(this.authStore.isImpersonating() ? { Impersonation: `Bearer ${impersonationToken}` } : {}),
        },
      });
    } else {
      if (this.authStore.isAuthenticated()) {
        const token: string = this.authStore.authorizationHeaderValue();
        const dpopToken = this.dpopTokenService.generateDpop(request.url, request.method);
        
        if (dpopToken instanceof Promise) {
          return from(dpopToken).pipe(
            switchMap((dpop) => {
              const updatedRequest = request.clone({
                setHeaders: {
                  jwt: this.authStore.getAccessToken() ?? '',
                  dpop: dpop ?? '',
                  Authorization: token,
                  Env: get(config, 'environment'),
                  ...csrfHeaders,
                  ...(this.authStore.isImpersonating() ? { Impersonation: `dpop ${impersonationToken}` } : {}),
                },
              });
              return next.handle(updatedRequest);
            }),
            catchError((error: HttpErrorResponse) => {
              if (error.status === HTTP_STATUS_UNAUTHORIZED || error.status === HTTP_STATUS_FORBIDDEN) {
                this.router.navigate(['/notauthorized']);
              }
              return throwError(() => error);
            })
          );
        } else {
          request = request.clone({
            setHeaders: {
              jwt: this.authStore.getAccessToken() ?? '',
              dpop: dpopToken ?? '',
              Authorization: token,
              Env: get(config, 'environment'),
              ...csrfHeaders,
              ...(this.authStore.isImpersonating() ? { Impersonation: `dpop ${impersonationToken}` } : {}),
            },
          });
        }
      } else {
        if (request.url.indexOf('/token') > TOKEN_NOT_FOUND) {
          const dpopToken = this.dpopTokenService.generateAuthDpop(request.url.trim(), request.method);
          
          if (dpopToken instanceof Promise) {
            return from(dpopToken).pipe(
              switchMap((dpop) => {
                const updatedRequest = request.clone({
                  setHeaders: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    dpop: dpop ?? '',
                    Env: get(config, 'environment'),
                    ...csrfHeaders,
                  },
                });
                return next.handle(updatedRequest);
              }),
              catchError((error: HttpErrorResponse) => {
                if (error.status === HTTP_STATUS_UNAUTHORIZED || error.status === HTTP_STATUS_FORBIDDEN) {
                  this.router.navigate(['/notauthorized']);
                }
                return throwError(() => error);
              })
            );
          } else {
            request = request.clone({
              setHeaders: {
                'Content-Type': 'application/x-www-form-urlencoded',
                dpop: dpopToken ?? '',
                Env: get(config, 'environment'),
                ...csrfHeaders,
              },
            });
          }
        }
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === HTTP_STATUS_UNAUTHORIZED || error.status === HTTP_STATUS_FORBIDDEN) {
          // Redirect to login page on 401/403
          this.router.navigate(['/notauthorized']);
        }
        return throwError(() => error);
      }));
  }

  /**
   * Get CSRF headers for state-changing HTTP methods
   * @param method - HTTP method
   * @returns Headers object with CSRF token if required
   */
  private getCsrfHeaders(method: string): Record<string, string> {
    if (CSRF_PROTECTED_METHODS.has(method.toUpperCase())) {
      return {
        [this.csrfService.getHeaderName()]: this.csrfService.getToken()
      };
    }
    return {};
  }

  override afterOnDestroy(): void {
    // afterOnDestroy
  }
}
